import * as React from "react";

import { Fieldset, Slider, TextInput } from "@/shared/foundation/primitives";

import { ParameterError } from "./ParameterError";
import styles from "./parameterForms.module.css";
import { useParameterRecord } from "./parameterValues";

const KEY = "RESIZE_IMAGES";
const MIN_PERCENT = 10;
const MAX_PERCENT = 100;

function percent(scale: number): string {
  return `${Math.round(scale * 100)}%`;
}

/**
 * The scale images are stored at, 10% to 100% of their original size, set by
 * dragging the slider or typing a percentage.
 */
export function ResizeImagesSlider({
  name,
  defaultScale,
}: {
  name: string;
  defaultScale: string;
}) {
  const record = useParameterRecord(name);
  const legendId = React.useId();
  const fallback = Number.parseFloat(defaultScale);
  const parsed = Number.parseFloat(record.get(KEY) ?? defaultScale);
  const scale = Number.isNaN(parsed) ? fallback : parsed;
  const scalePercent = Math.round(scale * 100);

  // What is typed in the box, kept apart from the stored value so a partly
  // typed number ("7" on the way to "75") is not written or overwritten.
  const [typed, setTyped] = React.useState(String(scalePercent));
  const [typing, setTyping] = React.useState(false);
  React.useEffect(() => {
    if (!typing) {
      setTyped(String(scalePercent));
    }
  }, [scalePercent, typing]);

  const typedPercent = Number(typed);
  const typedIsValid =
    typed.trim() !== "" &&
    Number.isFinite(typedPercent) &&
    typedPercent >= MIN_PERCENT &&
    typedPercent <= MAX_PERCENT;

  const writeScale = (next: number) => {
    const rounded = Math.round(next * 100) / 100;
    record.write({ [KEY]: rounded === fallback ? undefined : String(rounded) });
  };

  const commitTyped = () => {
    setTyping(false);
    if (typedIsValid) {
      writeScale(typedPercent / 100);
    } else {
      setTyped(String(scalePercent));
    }
  };

  return (
    <Fieldset.Root data-testid={`param-${KEY}`}>
      <Fieldset.Legend id={legendId}>Image size</Fieldset.Legend>
      <div className={styles.sliderRow}>
        <Slider
          className={styles.sliderControl}
          value={scale}
          onValueChange={(next) => {
            setTyping(false);
            writeScale(next);
          }}
          min={MIN_PERCENT / 100}
          max={MAX_PERCENT / 100}
          step={0.05}
          aria-labelledby={legendId}
          getAriaValueText={percent}
        />
        <span className={styles.percentField}>
          <TextInput
            size="sm"
            type="number"
            inputMode="numeric"
            min={MIN_PERCENT}
            max={MAX_PERCENT}
            step={1}
            value={typed}
            aria-labelledby={legendId}
            aria-invalid={typing && !typedIsValid}
            onChange={(event) => {
              setTyping(true);
              setTyped(event.target.value);
              const next = Number(event.target.value);
              if (
                event.target.value.trim() !== "" &&
                Number.isFinite(next) &&
                next >= MIN_PERCENT &&
                next <= MAX_PERCENT
              ) {
                writeScale(next / 100);
              }
            }}
            onBlur={commitTyped}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitTyped();
              }
            }}
          />
          <span aria-hidden="true">%</span>
        </span>
      </div>
      <Fieldset.Description>
        {typing && !typedIsValid
          ? `Enter a whole number from ${MIN_PERCENT} to ${MAX_PERCENT}.`
          : `Images are stored at ${percent(scale)} of their original size.`}
      </Fieldset.Description>
      <ParameterError message={record.error(KEY)} />
    </Fieldset.Root>
  );
}
